import { Amount } from '@emberswap/emberswap-sdk2/dist/entities'
import { ChainId } from '@emberswap/emberswap-sdk2/dist/enums'
import { Currency, Type } from '@emberswap/emberswap-sdk2/dist/types'
import React, { FC, memo, useMemo } from 'react'
import { zeroAddress } from 'viem'
import { useAccount } from 'wagmi'

import { classNames } from '../../functions'
import { usePrices } from '../../hooks/usePrices'
import { TokenAsObject } from '../../state/user/reducer'
import { Currency as UICurrency} from '../Currency'
import { TokenSelectorRow } from './TokenSelectorRow'

interface TokenSelectorCurrencyListProps {
  currencies: Type[] | undefined
  chainId: ChainId
  officialTokenIds: string[]
  onSelect(currency: Type): void
  pin?: {
    isPinned: (currencyId: string) => boolean
    onPin: (currencyId: string) => void
  }
  selected: Type | undefined
  balancesMap: Record<string, Amount<Currency>>
  midSelector?: boolean
  onPath?:{
    onRemovePath: (currency: Currency) => void
    isPath: (currency: Currency) => boolean
  }
  userPath?: Record<number, Record<string, TokenAsObject>>
  disableNative?: boolean
}

export const TokenSelectorCurrencyList: FC<TokenSelectorCurrencyListProps> = memo(
  function TokenSelectorCurrencyList({
    onSelect,
    currencies,
    chainId,
    selected,
    pin,
    officialTokenIds,
    balancesMap,
    midSelector,
    onPath,
    userPath,
    disableNative,
  }) {
    const { address } = useAccount()
    const { data: pricesMap } = usePrices({ chainId })

    const rowData = useMemo<TokenSelectorRow[]>(() => {
      if (!currencies) return []

      return currencies.map((currency) => ({
        disableNative: disableNative,
        className: classNames(
          '!pl-3 !pr-6',
        midSelector ? '': 'rounded-xl px-4',
          midSelector &&
            userPath &&
            userPath[chainId] &&
            Object.values(userPath[chainId]).some((t) => t.address == currency.wrapped.address)
            ? '!bg-slate-700 '
            : ''
        ),
        midSelector: midSelector,
        account: address,
        currency: currency,
        balance: balancesMap?.[currency.isNative ? zeroAddress : currency.address],
        price: pricesMap?.[currency.isNative ? zeroAddress : currency.wrapped.address],
        showWarning: currency.isNative ? false : !officialTokenIds.includes(currency.address),
        onSelect: () => onSelect(currency),
        pin: pin
          ? {
              onPin: () => pin?.onPin(currency.id),
              isPinned: pin.isPinned(currency.id),
            }
          : undefined,
        onPath: onPath
          ? {
              onRemovePath: () => onPath.onRemovePath(currency),
              isPath: onPath.isPath(currency),
            }
          : undefined,
        selected: selected
          ? (currency.isNative === true && selected.isNative === true) ||
            (selected.isToken &&
              currency.isToken &&
              currency.wrapped.address === selected.wrapped.address)
          : false,
      }))
    }, [currencies, disableNative, midSelector, userPath, chainId, address, balancesMap, pricesMap, officialTokenIds, pin, onPath, selected, onSelect])

    return (
      <UICurrency.List
        className="scroll"
        rowHeight={48}
        rowRenderer={TokenSelectorRow}
        rowData={rowData}
      />
    )
  }
)
